import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useAuthContext } from "./AuthProvider";
import { useConfigContext } from "./ConfigProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { z, ZodObject } from "zod";
import generateValidationSchema from "../helpers/schema";
import { collectDefaultValueElements, isEmptyValue } from "../helpers/autoupdate";
import { ApplicationItemType, FileType, FormDataType, FormType, OptionType, ReportItemType, ReportType, SectionType } from "../helpers/types";
import { mapUploadedFiles, replaceUploadedFiles } from "../helpers/fileFields";

type ContextType = {
    application: ApplicationItemType | null;
    reportId: number | null;
    reportType: string | null;
    reportStatus: number | null;
    applicationId: number | null;
    section: SectionType | null;
    form: FormType | null;
    formData: FormDataType;
    report: any;
    schema: z.ZodObject<any> | null;
    errors: FormDataType;
    consentApproved: boolean;
    contextDone: boolean;
    draftIds: ReportItemType[] | null;
    allValid: boolean;
    setConsentApproved: (value: boolean) => void;
    setReportData: (type: string, id: number) => void;
    setReportVersion: (value: number) => void;
    setAllValid: (value: boolean) => void;
    next: () => void;
    prev: () => void;
    updateForm: (name: string, value: string | number | boolean | OptionType[] | FileType | null, isFile?: boolean) => void;
    updateErrors: (name: string, value: string | number | null) => void;
    saveDraft: () => Promise<unknown>;
    getDraftFromDataBase: () => Promise<string | null>;
    deleteDraftFromDataBase: (reportType: string | null, draftId: number | null) => void;
    checkForIncompleteSection: (url: string) => Promise<string | null>;
}

const NewReportContext = createContext<ContextType | null>(null);

const useNewReportContext = () => {
    const context = useContext(NewReportContext);
    if (context === null) {
        throw new Error('useNewReportContext must me used within a NewReportProvider');
    }

    return context;
}

const NewReportProvider = ({ children }: { children: ReactNode }) => {
    const { apiCall, user } = useAuthContext();
    const { reportData, reportDraftId, setReportDraftId } = useConfigContext();

    const [reportId, setReportId] = useState<number | null>(null);
    const [reportType, setReportType] = useState<string | null>(null);
    const [reportVersion, setReportVersion] = useState<number | null>(null);
    const [reportStatus, setReportStatus] = useState<number | null>(null);
    const [applicationId, setApplicationId] = useState<number | null>(null);
    const [draftIds, setDraftIds] = useState<ReportItemType[] | null>(null);

    const [application, setApplication] = useState<ApplicationItemType | null>(null);
    const [report, setReport] = useState<ReportType | null>(null);
    const [formData, setFormData] = useState<FormDataType>({});
    const [lastSavedData, setLastSavedData] = useState<FormDataType>({});
    const [errors, setErrors] = useState<FormDataType>({});
    const [allValid, setAllValid] = useState<boolean>(false);

    const [section, setSection] = useState<SectionType | null>(null);
    const [form, setForm] = useState<FormType | null>(null);

    const [schema, setSchema] = useState<ZodObject<any> | null>(null);
    const [contextDone, setContextDone] = useState(false);
    const [consentApproved, setConsentApproved] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // reportId (state) only updates after a render, so two saveDraft() calls
    // fired back-to-back (e.g. quick section navigation) can both still see
    // reportId as null and each create a separate report row. The ref is
    // updated synchronously, and the queue below serializes the calls so the
    // first one's response (and reportIdRef update) lands before the next runs.
    const reportIdRef = useRef<number | null>(null);
    const saveDraftQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    // Alle File → lagret beskrivelse-bytter i denne økta. Se saveDraft.
    const uploadedSwapsRef = useRef(new Map<File, Record<string, unknown>>());

    const checkDrafts = async () => {
        // Checks if the user has any drafts
        try {
            const response = await apiCall({
                action: 'checkForReportDraft',
                reportTemplate: reportVersion,
                applicationId: applicationId
            })

            const data = await response.data;

            if (data.status === 200) {
                if (data.drafts.length === 1) {
                    const draft = data.drafts[0];
                    // If 1 draft
                    setReportDraftId(draft.id);
                    setReportStatus(draft.status);
                    setDraftIds(null);
                } else {
                    // If more than 2 drafts
                    setDraftIds(data.drafts);
                }
            } else {
                setReportDraftId(null);
                setReportStatus(null);
            }
        } catch (error) {
            console.error("Checkdrafts ERRPR:", error)
        } finally {
            setContextDone(true);
        }
    }

    const loadApplication = async () => {
        const { data } = await apiCall({
            action: 'getApplicationDetailsById',
            id: params.id,
            user_id: user?.id
        });

        if (data.status === 200) {
            setApplication(data.data);
        }
    }

    useEffect(() => {
        if (report) {
            if (params.formType && params.id) {
                const currentReport = reportData?.filter((report: ReportType) => report.url === params.formType)
                if (currentReport) {
                    setReport(currentReport[0]);
                    setReportType(currentReport[0].type);
                    setApplicationId(parseInt(params.id));
                }
            }

            if (params.section) {
                if (report.sections.filter(section => section.url === params.section).length) {
                    setSection(report.sections.filter(section => section.url === params.section)[0])
                }
            } else {
                setSection(null);
            }

            if (params.form) {
                if (section) {
                    if (section.forms && section.forms.length) {
                        if (section.forms.filter(form => form.url === params.form).length) {
                            setForm(section.forms.filter(form => form.url === params.form)[0])
                        }
                    }
                }
            } else {
                setForm(null)
            }
        }
    }, [location]);

    useEffect(() => {
        if (report) {
            if (params.section) {
                if (report.sections.filter(section => section.url === params.section).length) {
                    setSection(report.sections.filter(section => section.url === params.section)[0])
                }
            } else {
                setSection(null)
            }

            // Betingelser kan baseres på hvilken søknadsmal søknaden er levert
            // på — send med søknadens type så valideringen evaluerer dem riktig.
            setSchema(generateValidationSchema(report, { applicationType: application?.type }))
        }
    }, [report, application])

    // Predefinert verdi (defaultValue): seedes inn i formData når feltet er
    // tomt — samme mekanikk som i søknadsskjemaet (bl.a. for `hidden`-felt).
    useEffect(() => {
        if (!report) return;
        const elements = collectDefaultValueElements(report);
        if (elements.length === 0) return;

        const patch: FormDataType = {};
        for (const el of elements) {
            if (!isEmptyValue(formData[el.name])) continue;
            patch[el.name] = (el as any).defaultValue;
        }

        if (Object.keys(patch).length === 0) return;
        setFormData((prev) => ({ ...prev, ...patch }));
    }, [formData, report])

    useEffect(() => {
        if (params.form) {
            if (section) {
                if (section.forms && section.forms.length) {
                    if (section.forms.filter(form => form.url === params.form).length) {
                        setForm(section.forms.filter(form => form.url === params.form)[0])
                    }
                }
            }
        } else {
            setForm(null)
        }
    }, [section])

    useEffect(() => {
        if (reportVersion) {
            checkDrafts()
        }
    }, [reportVersion])

    useEffect(() => {
        loadApplication();
    }, []);

    return (
        <NewReportContext.Provider value={{
            application,
            reportId,
            reportType,
            reportStatus,
            applicationId,
            section,
            form,
            formData,
            report,
            schema,
            errors,
            consentApproved,
            setConsentApproved,
            setReportVersion,
            contextDone,
            draftIds,
            allValid,
            setAllValid,
            setReportData: (type, id) => {
                if (reportData) {
                    const currentReport = reportData.filter((report: ReportType) => report.url === type)
                    setReport(currentReport[0])
                    setReportType(currentReport[0].type)
                    setReportVersion(currentReport[0].template_id)
                    setApplicationId(id)

                }
            },
            next: () => {
                
                // If section does not exists we need to navigate to the very first form and section
                if (!section) {
                    if (report?.sections[0].forms && report.sections[0].forms.length) {
                        navigate(`/rapport/${applicationId}/${report.url}/${report.sections[0].url}/${report.sections[0].forms[0].url}`)
                    }else{
                        navigate(`/rapport/${applicationId}/${report.url}/${report.sections[0].url}`)
                    }

                    return;
                }

                // If section and form exists we need to find out if there are any more forms in the current section
                if (form) {
                    const formIndex = section.forms.findIndex(f => f.url === form.url);
                    if (formIndex !== undefined && section.forms[formIndex + 1]) {
                        // There are more forms
                        navigate(`/rapport/${applicationId}/${report?.url}/${section.url}/${section.forms[formIndex + 1].url}`)
                    } else {
                        // There are noe more forms in the section
                        const sectionIndex = report?.sections.findIndex(s => s.url === section.url);
                        if (sectionIndex !== undefined && report?.sections[sectionIndex + 1]) {
                            // Checks if section has forms, else navigate to just section
                            if (report.sections[sectionIndex + 1].forms) {
                                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex + 1].url}/${report.sections[sectionIndex + 1].forms[0].url}`)
                            } else {
                                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex + 1].url}`)
                            }
                        }
                    }
                } else {
                        const sectionIndex = report?.sections.findIndex((s) => s.url === section.url);
                        if (sectionIndex !== undefined && report?.sections[sectionIndex + 1]) {
                            if (report?.sections[sectionIndex + 1].forms && report.sections[sectionIndex + 1].forms.length) {
                                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex + 1].url}/${report.sections[sectionIndex + 1].forms[0].url}`);
                            } else {
                                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex + 1].url}`);
                            }
                        }
                    }
            },
            prev: () => {
                // If section does not exists we need to navigate to the very first form and section
                if (!section) {
                    if (report?.sections[0].forms && report.sections[0].forms.length) {
                        navigate(`/rapport/${applicationId}/${report.url}/${report.sections[0].url}/${report.sections[0].forms[0].url}`)
                    }

                    return;
                }

                // If section and form exists we need to find out if there are any more forms in the current section
                if (form) {
                    const formIndex = section.forms.findIndex(f => f.url === form.url);
                    if (formIndex !== undefined && section.forms[formIndex - 1]) {
                        // There are more forms
                        navigate(`/rapport/${applicationId}/${report?.url}/${section.url}/${section.forms[formIndex - 1].url}`)
                    } else {
                        // There are noe more forms in the section
                        const sectionIndex = report?.sections.findIndex(s => s.url === section.url);
                        if (sectionIndex !== undefined && report?.sections[sectionIndex - 1]) {
                            // Checks if section has forms, else navigate to just section
                            if (report.sections[sectionIndex - 1].forms.length) {
                                // Find the last form of the section
                                const numberOfForms = report.sections[sectionIndex - 1].forms.length;
                                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex - 1].url}/${report.sections[sectionIndex - 1].forms[numberOfForms - 1].url}`)
                            } else {
                                navigate(`/rapport/${applicationId}/${report.url}/${report.sections[sectionIndex - 1].url}`)
                            }
                        }
                    }
                }
            },
            updateForm: (field: string, value: string | number | boolean | OptionType[] | FileType | null) => {
                setFormData((prev) => {
                    if (value === null) {
                        // If value is null, remove the field from the state object
                        const { [field]: _, ...newFormData } = prev;
                        return newFormData;
                    }

                    // Otherwise, update the state as usual
                    return {
                        ...prev,
                        [field]: value
                    };
                });
            },
            updateErrors: (field: string, value: string | number | null) => {
                setErrors((prev) => {
                    if (value === null) {
                        // If value is null, remove the field from the state object
                        const { [field]: _, ...newErrorsData } = prev;
                        return newErrorsData;
                    }

                    // Otherwise, update the state as usual
                    return {
                        ...prev,
                        [field]: value,
                    };
                });
            },
            saveDraft: () => {
                const run = async () => {
                    // En lagring som ble satt i kø før forrige var ferdig, har
                    // fortsatt File-ene i sin kopi av skjemaet — bytt dem først,
                    // ellers lastes de opp igjen under ny `location`.
                    const currentData = replaceUploadedFiles(formData, uploadedSwapsRef.current);

                    // Prevent saving if data hasn't changed
                    if (JSON.stringify(currentData) === JSON.stringify(lastSavedData)) {
                        return;
                    }

                    let object: Record<string, any> = {};
                    for (let key in currentData) {
                        object[key] = currentData[key]
                    }

                    try {
                        // Save draft to server
                        const response = await apiCall({
                            action: 'saveReportDraft',
                            reportId: reportIdRef.current,
                            reportType: reportType,
                            applicationId: applicationId,
                            reportVersion: reportVersion,
                            ...object
                        });

                        // `apiCall` gir axios-responsen — API-ets svar ligger i
                        // `.data`. Leses id-en fra feil nivå, blir den aldri satt,
                        // og hver lagring oppretter en ny rapportrad.
                        const savedReportId = response?.data?.report_id;
                        if (!reportIdRef.current && savedReportId) {
                            reportIdRef.current = savedReportId;
                            setReportId(savedReportId);
                        }

                        // Filene som ble lastet opp — også bilag i utleggsrader —
                        // byttes mot det API-et lagret, ellers sendes de på nytt
                        // ved hver lagring.
                        const swaps = mapUploadedFiles(object, response?.data?.data?.uploaded);
                        if (swaps.size > 0) {
                            swaps.forEach((stored, file) => uploadedSwapsRef.current.set(file, stored));
                            setFormData((prev) => replaceUploadedFiles(prev, swaps));
                        }

                        // Update the last saved data
                        setLastSavedData(replaceUploadedFiles(currentData, swaps));

                        return response;
                    } catch (error) {
                        console.error("Savedraft Error: ", error)
                    }
                };

                // Queue behind any save already in flight so the report row
                // created by the first call is reused instead of racing it.
                const next = saveDraftQueueRef.current.then(run, run);
                saveDraftQueueRef.current = next;
                return next;
            },
            getDraftFromDataBase: async () => {
                const response = await apiCall({
                    action: 'getReportDraftFromDB',
                    reportId: reportDraftId
                });

                const data = response.data.data;
                setFormData(data);
                reportIdRef.current = reportDraftId;
                setReportId(reportDraftId);

                if (!report || !schema) {
                    console.warn('Application or schema is missing.');
                    return null;
                }

                let lastIncompleteUrl: string | null = null;

                // Iterate through sections and forms
                for (const section of report.sections) {
                    if (!section.forms || section.forms.length === 0) {
                        continue;
                    }

                    for (const form of section.forms) {
                        const sectionSchema = schema.shape[section.url];
                        const formSchema = sectionSchema.shape[form.url] ?? sectionSchema._def?.schema?.shape[form.url];

                        if (!formSchema) {
                            console.warn(`Schema missing for form: ${form.url}`);
                            continue;
                        }

                        // Transforming some values from database
                        form.elements.forEach((element) => {
                            if (data[element.name]) {
                                switch (element.type) {
                                    case 'dropdowncheck':
                                        const parsedArray = JSON.parse(data[element.name]);
                                        data[element.name] = parsedArray;
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            [element.name]: parsedArray
                                        }))
                                        break;
                                    case 'fileuploadsingle':
                                        const newValue = data[element.name][0];
                                        data[element.name] = newValue;
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            [element.name]: newValue
                                        }));
                                        break;
                                }
                            }
                        })

                        // Validate the form data
                        const valid = formSchema.safeParse(data);
                        if (!valid.success) {
                            lastIncompleteUrl = `${section.url}/${form.url}`;
                            return lastIncompleteUrl; // Navigate to the first invalid form
                        }
                    }
                }

                // If all forms are valid, return the last form URL
                if (report.sections.length > 0) {
                    const lastSection = report.sections.at(-1);
                    //const lastForm = lastSection?.forms?.at(-1);
                    if (lastSection) {
                        lastIncompleteUrl = `${lastSection.url}`;
                    }
                }

                return lastIncompleteUrl;
            },
            deleteDraftFromDataBase: async (reportType, draftId) => {
                const response = await apiCall({
                    action: 'deleteReportDraftFromDB',
                    draftId,
                    reportType
                });

                if (response.data.status === 200) {
                    setReportDraftId(null);

                    if (report?.sections[0].forms && report.sections[0].forms.length) {
                        navigate(`/rapport/${applicationId}/${report.url}/${report.sections[0].url}/${report.sections[0].forms[0].url}`)
                    }
                }

                return response;
            },
            checkForIncompleteSection: async (sectionUrl: string) => {
                if (!report || !schema) {
                    return null;
                }

                const isSummary = sectionUrl === 'oppsummering';

                if (!isSummary) {
                    const targetSection = report.sections.find(s => s.url === sectionUrl);
                    if (!targetSection || !targetSection.forms || targetSection.forms.length === 0) {
                        return null;
                    }
                }

                for (const section of report.sections) {
                    if (!section.forms || section.forms.length === 0) {
                        continue;
                    }

                    for (const form of section.forms) {
                        const sectionSchema = schema.shape[section.url];
                        const formSchema = sectionSchema?.shape?.[form.url] ?? sectionSchema?._def?.schema?.shape?.[form.url];

                        if (!formSchema) {
                            continue;
                        }

                        const valid = formSchema.safeParse(formData);
                        if (!valid.success) {
                            if (isSummary) {
                                return null;
                            }

                            if (section.url === sectionUrl) {
                                return `${section.url}/${form.url}`;
                            }

                            return null;
                        }
                    }

                    if (section.url === sectionUrl && !isSummary) {
                        return `${sectionUrl}/${section.forms[0].url}`;
                    }
                }

                // If all previous sections are valid and the target is summary, allow navigation
                if (isSummary) {
                    return sectionUrl;
                }

                return null;
            }
        }}>
            {children}
        </NewReportContext.Provider>
    )
}

export { useNewReportContext, NewReportProvider };